import { useEffect, useRef, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useSelector } from "react-redux";

import { Portal } from "@material-ui/core";
import { BUBBLE_MIN_ZOOM } from "constants/mapLayers.constants";
import { RootState } from "store/rootReducer";

import { EntityKind } from "models/entityKind";
import { WidgetComponentType } from "models/screenshot";

import { useScreenshotContext, useScreenshotDispatch } from "components/screenshot/hooks";
import { LegendPosition } from "components/shared/charts/legend/utils/getPositionFromChartSettings";

import TownshipRangeGrid from "../TownshipRangeGrid";
import { useMapContext } from "../hooks/useMapContext";
import { useMapDispatch } from "../hooks/useMapDispatch";
import { BubbleVisLegend } from "../toolbar/BubbleVisLegend";
import "./Screenshot.scss";
import { usePreScreenshotView } from "./hooks/usePreScreenshotView";
import { IpdbLegendWrapper } from "./legend/IpdbLegendWrapper";
import LegendWrapper from "./legend/LegendWrapper";
import { NetworkDepthLegendWrapper } from "./legend/NetworkDepthLegendWrapper";
import { ShapefileLegendWrapper } from "./legend/shapefile-legend";
import { screenshotModePayload } from "./screenshotModePayload";

const DEFAULT_FRAME_WIDTH = 1152;
const DEFAULT_FRAME_HEIGHT = 681;

interface ScreenshotProps {
  setScreenshotOverlayVisible: (visible: boolean) => void;
  screenshotOverlayVisible: boolean;
}

export default function Screenshot({
  setScreenshotOverlayVisible,
  screenshotOverlayVisible
}: ScreenshotProps) {
  const { mapbox } = useMapContext();
  // get activeEntityKinds from redux store
  const activeEntityKinds = useSelector(
    (state: RootState) => state.app.activeEntityKinds
  );

  const mapDispatch = useMapDispatch();
  const screenshotDispatch = useScreenshotDispatch();

  const [isBubbleLegendVisible, setIsBubbleLegendVisible] = useState(false);
  const copyOverlayRef = useRef<HTMLDivElement>(null);

  const { settings, widget } = useScreenshotContext();

  usePreScreenshotView({ mapbox, isComposing: screenshotOverlayVisible });

  useEffect(() => {
    const screenshotActive = widget?.widgetId === "mb";
    setScreenshotOverlayVisible(screenshotActive);
    mapDispatch({ payload: screenshotModePayload(screenshotActive) });
  }, [mapDispatch, settings, widget]);

  useHotkeys(
    "esc",
    () => {
      screenshotDispatch({
        payload: {
          widget: null,
          widgetState: null
        }
      });
    },
    [mapbox]
  );

  useEffect(() => {
    mapDispatch({ payload: screenshotModePayload(screenshotOverlayVisible) });

    const widget = screenshotOverlayVisible
      ? {
          widgetId: "mb",
          widgetComponentType: WidgetComponentType.Map
        }
      : null;

    screenshotDispatch({
      payload: {
        widget
      }
    });
  }, [mapDispatch, screenshotDispatch, screenshotOverlayVisible]);

  useEffect(() => {
    if (screenshotOverlayVisible) {
      screenshotDispatch({
        payload: {
          widgetState: {
            mapbox,
            copyOverlay: copyOverlayRef.current,
            activeEntityKinds
          }
        }
      });
    }
  }, [screenshotDispatch, screenshotOverlayVisible, mapbox, activeEntityKinds]);

  useEffect(() => {
    if (!mapbox) {
      return;
    }

    function onZoomEnd() {
      if (!mapbox) {
        return;
      }
      const zoom = mapbox.getZoom();
      setIsBubbleLegendVisible(zoom > BUBBLE_MIN_ZOOM);
    }

    mapbox.on("zoomend", onZoomEnd);
    return () => {
      if (mapbox) {
        mapbox.off("zoomend", onZoomEnd);
      }
    };
  }, [mapbox]);

  // render
  if (!mapbox) return null;

  const screenshotBounds = {
    width: (settings?.width || DEFAULT_FRAME_WIDTH) * (settings?.mapScreenshotScale ?? 1),
    height:
      (settings?.height || DEFAULT_FRAME_HEIGHT) * (settings?.mapScreenshotScale ?? 1)
  };

  return (
    <div className="Screenshot">
      <Portal container={document.getElementById("map-screenshot-overlay")}>
        {screenshotOverlayVisible && (
          <div className="copy-overlay-container ">
            <div
              className="copy-overlay"
              ref={copyOverlayRef}
              style={{
                width: `calc(${screenshotBounds.width}px - 2px)`,
                height: `calc(${screenshotBounds.height}px - 2px)`
              }}>
              <div className="buttons" />
              <LegendWrapper
                width={250}
                parentDimensions={screenshotBounds}
                isScreenshotOverlayVisible={screenshotOverlayVisible}
                isCaptureFrameVisible={screenshotOverlayVisible}
                captureFrameRef={copyOverlayRef}
                entityKind={EntityKind.Well}
                defaultPosition={LegendPosition.NE}
              />
              <LegendWrapper
                width={250}
                parentDimensions={screenshotBounds}
                isScreenshotOverlayVisible={screenshotOverlayVisible}
                isCaptureFrameVisible={screenshotOverlayVisible}
                captureFrameRef={copyOverlayRef}
                entityKind={EntityKind.Facility}
                defaultPosition={LegendPosition.NW}
              />
              <IpdbLegendWrapper isScreenshotOverlayVisible={screenshotOverlayVisible} />
              <NetworkDepthLegendWrapper
                isScreenshotOverlayVisible={screenshotOverlayVisible}
              />
              <ShapefileLegendWrapper
                isScreenshotOverlayVisible={screenshotOverlayVisible}
              />
              {settings?.showTownshipBorderInScreenshot && (
                <TownshipRangeGrid containerSize={screenshotBounds}></TownshipRangeGrid>
              )}
              {isBubbleLegendVisible && (
                <BubbleVisLegend
                  screenshotLegend={true}
                  parentDimensions={{
                    width: screenshotBounds.width,
                    height: screenshotBounds.height
                  }}
                />
              )}
            </div>
          </div>
        )}
      </Portal>
    </div>
  );
}
